import { useRef, useState, useCallback } from "react";
import { Upload, Camera, FileText, AlertCircle, Loader2 } from "lucide-react";
import { Emery } from "@/components/ui/Calla";
import {
  uploadAndExtract,
  ACCEPT_ATTRIBUTE,
  type ClientExtractionResult,
} from "@/lib/decPageExtraction";
import type { ExtractionResponse } from "@/types/extraction";

type UploadStage =
  | { kind: "idle" }
  | { kind: "uploading" }
  | { kind: "reading" }
  | { kind: "error"; message: string; retryable: boolean };

interface DecPageUploaderProps {
  onExtracted: (result: ExtractionResponse) => void;
  onFallbackToManual: () => void;
}

const EMERY_UPLOADING =
  "Got it. Give me a few seconds to read your policy.";

export function DecPageUploader({
  onExtracted,
  onFallbackToManual,
}: DecPageUploaderProps) {
  const [stage, setStage] = useState<UploadStage>({ kind: "idle" });
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback(
    async (file: File) => {
      setStage({ kind: "uploading" });
      const result: ClientExtractionResult = await uploadAndExtract(
        file,
        (progressStage) => {
          setStage({ kind: progressStage });
        },
      );

      if (result.ok) {
        onExtracted(result);
        return;
      }

      const retryable = "retryable" in result ? result.retryable : true;
      setStage({
        kind: "error",
        message: result.error,
        retryable,
      });
    },
    [onExtracted],
  );

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void processFile(file);
    }
    // Reset so the same file can be selected again after an error
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const isBusy = stage.kind === "uploading" || stage.kind === "reading";

  return (
    <div className="max-w-lg mx-auto">
      {isBusy && <Emery message={EMERY_UPLOADING} />}

      <div className="step-card">
        {stage.kind === "idle" && (
          <>
            <h2
              className="mb-2 text-xl font-semibold"
              style={{ color: "var(--color-navy)" }}
            >
              Upload your declarations page
            </h2>
            <p
              className="mb-6 text-sm"
              style={{ color: "var(--text-secondary)" }}
            >
              Your current auto policy summary. Most carriers email this at
              renewal, or you can find it in your carrier's app or website.
            </p>

            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={() => setIsDragActive(true)}
              onDragLeave={() => setIsDragActive(false)}
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              style={{
                border: `2px dashed ${
                  isDragActive
                    ? "var(--color-primary)"
                    : "var(--border-default, #D1D5DB)"
                }`,
                borderRadius: 16,
                padding: "32px 24px",
                textAlign: "center",
                cursor: "pointer",
                background: isDragActive
                  ? "var(--bg-primary-subtle, #F0FDF4)"
                  : "var(--bg-muted, #FAFAFA)",
                transition: "border-color 0.15s ease, background 0.15s ease",
              }}
            >
              <Upload
                size={32}
                strokeWidth={1.5}
                style={{
                  color: "var(--color-primary)",
                  margin: "0 auto 12px",
                  display: "block",
                }}
              />
              <div
                className="font-medium mb-1"
                style={{ color: "var(--color-navy)" }}
              >
                Tap to choose a file
              </div>
              <div
                className="text-xs"
                style={{ color: "var(--text-muted)" }}
              >
                or drag it here. PDF, JPEG, PNG, HEIC up to 20MB.
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ATTRIBUTE}
              onChange={handleFileSelected}
              style={{ display: "none" }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelected}
              style={{ display: "none" }}
            />

            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              className="w-full mt-4"
              style={{
                border: "1px solid var(--border-default, #E5E7EB)",
                background: "white",
                padding: "12px",
                borderRadius: 12,
                color: "var(--color-navy)",
                fontSize: 14,
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                cursor: "pointer",
              }}
            >
              <Camera size={18} strokeWidth={2} />
              Take a photo instead
            </button>

            <button
              type="button"
              onClick={onFallbackToManual}
              className="w-full mt-4 text-sm"
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-secondary)",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Skip upload and fill in manually
            </button>
          </>
        )}

        {stage.kind === "uploading" && (
          <StageMessage
            title="Uploading your policy"
            subtitle="Encrypting and sending to our secure storage."
          />
        )}

        {stage.kind === "reading" && (
          <StageMessage
            title="Reading your policy"
            subtitle="This usually takes about 10 seconds."
          />
        )}

        {stage.kind === "error" && (
          <div>
            <div
              className="mb-4 flex items-start gap-3 rounded-lg p-4"
              style={{
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                color: "#991B1B",
              }}
            >
              <AlertCircle size={20} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div className="font-medium mb-1">
                  We couldn't read your policy
                </div>
                <div className="text-sm">{stage.message}</div>
              </div>
            </div>

            <div className="flex gap-3">
              {stage.retryable && (
                <button
                  type="button"
                  onClick={() => setStage({ kind: "idle" })}
                  className="btn-primary flex-1"
                >
                  Try again
                </button>
              )}
              <button
                type="button"
                onClick={onFallbackToManual}
                className={stage.retryable ? "flex-1" : "btn-primary w-full"}
                style={
                  stage.retryable
                    ? {
                        border: "1px solid var(--border-default, #E5E7EB)",
                        background: "white",
                        padding: "12px",
                        borderRadius: 12,
                        color: "var(--color-navy)",
                        fontWeight: 500,
                        cursor: "pointer",
                      }
                    : undefined
                }
              >
                Fill in manually
              </button>
            </div>
          </div>
        )}
      </div>

      {!isBusy && stage.kind === "idle" && (
        <TipsCard />
      )}
    </div>
  );
}

function StageMessage({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <div className="py-8 text-center">
      <Loader2
        size={40}
        strokeWidth={1.5}
        className="animate-spin"
        style={{
          color: "var(--color-primary)",
          margin: "0 auto 16px",
          display: "block",
        }}
      />
      <div
        className="font-medium mb-2"
        style={{ color: "var(--color-navy)" }}
      >
        {title}
      </div>
      <div
        className="text-sm"
        style={{ color: "var(--text-secondary)" }}
      >
        {subtitle}
      </div>
    </div>
  );
}

function TipsCard() {
  return (
    <div
      className="mt-4 p-4"
      style={{
        background: "var(--bg-muted, #F9FAFB)",
        borderRadius: 12,
        border: "1px solid var(--border-subtle, #E5E7EB)",
      }}
    >
      <div className="flex items-start gap-3">
        <FileText
          size={18}
          strokeWidth={2}
          style={{
            color: "var(--text-secondary)",
            marginTop: 2,
            flexShrink: 0,
          }}
        />
        <div>
          <div
            className="text-sm font-medium mb-1"
            style={{ color: "var(--color-navy)" }}
          >
            What is a declarations page?
          </div>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            The summary page of your current auto policy. Shows your carrier,
            policy number, vehicles, drivers, coverages, and premium. Usually
            1-4 pages.
          </div>
        </div>
      </div>
    </div>
  );
}
